import React from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import VerticalTab from '/packages/shared-components/VerticalTab/VerticalTab';
import StatusFormChip from '/packages/shared-components/StatusFormChip/StatusFormChip';
import { TestSetsTabProps } from '../TestPlanActionsModal.types';
import {
  getSkuValue,
  hasIncompleteTransitionsInFormValues,
  hasActiveSetInFormValues,
  isSkuBoxValid,
} from '../validation.utils';

const TestSetsTab: React.FC<TestSetsTabProps> = ({
  selectedValue,
  onClick,
  control,
  enabled,
  goldStandardFormValues,
  hasSkuValue,
  goldStandardError,
  skuBoxStates,
  additionalSkuBoxes,
}) => {
  const planType = useWatch({ control, name: 'planType' });
  const additionalSkuValues = useWatch({
    control,
    name: additionalSkuBoxes?.map((skuId) => `sku_${skuId}`) || [],
  });

  const hasActiveSets = hasActiveSetInFormValues(goldStandardFormValues);
  const mainHasIncompleteTransitions = hasIncompleteTransitionsInFormValues(goldStandardFormValues);

  const additionalHasIncompleteTransitions = (additionalSkuBoxes || []).some((skuId) => {
    const boxState = skuBoxStates?.[`sku_${skuId}`];
    return hasIncompleteTransitionsInFormValues(boxState?.formValues);
  });

  const hasIncompleteTransitions = mainHasIncompleteTransitions || additionalHasIncompleteTransitions;

  const hasAdditionalErrors = additionalSkuBoxes?.some((skuId) => skuBoxStates?.[`sku_${skuId}`]?.hasError) || false;
  const hasMainError = skuBoxStates?.main?.hasError || goldStandardError || false;
  const hasErrors = hasMainError || hasAdditionalErrors;

  const allAdditionalSkusValid =
    additionalSkuBoxes?.every((skuId) => {
      const boxState = skuBoxStates?.[`sku_${skuId}`];
      const skuValue = additionalSkuValues?.[additionalSkuBoxes.indexOf(skuId)];
      return isSkuBoxValid(skuValue, boxState?.formValues);
    }) ?? true;

  const hasAnyValidSku =
    hasSkuValue ||
    (additionalSkuBoxes && additionalSkuBoxes.length > 0 && additionalSkuValues?.some((val) => getSkuValue(val)));

  const hasEmptySkuBoxes =
    (!hasSkuValue && additionalSkuBoxes && additionalSkuBoxes.length === 0) ||
    (additionalSkuBoxes && additionalSkuBoxes.length > 0 && !additionalSkuValues?.every((val) => getSkuValue(val)));

  const isFormReady =
    planType === 'auto'
      ? hasAnyValidSku &&
        (hasSkuValue ? hasActiveSets && !hasIncompleteTransitions : true) &&
        !hasErrors &&
        allAdditionalSkusValid &&
        !hasEmptySkuBoxes
      : true;

  const skuBoxAdded = additionalSkuBoxes && additionalSkuBoxes.length > 0;

  const { errors } = useFormState({ control });
  const hasSubmitError = !!errors.skuValidationError;

  return (
    <VerticalTab
      title="Test Sets"
      value={planType === 'auto' ? 1 : 2}
      selectedValue={selectedValue}
      onClick={onClick}
      disabled={!enabled}
      icon={
        <StatusFormChip
          control={control}
          fields={
            planType === 'auto'
              ? [
                  'globalTestSets',
                  ...(hasSubmitError ? ['skuValidationError'] : []),
                  ...(isFormReady ? [] : ['skuValue', '']),
                  ...(skuBoxAdded && !isFormReady ? ['skuAdded'] : []),
                ]
              : ['testSetIds']
          }
          requiredFields={
            planType === 'auto'
              ? [
                  ...(hasSubmitError ? ['skuValidationError'] : []),
                  ...(isFormReady ? [] : ['skuValue', '', ...(skuBoxAdded ? ['skuAdded'] : [])]),
                ]
              : ['testSetIds']
          }
        />
      }
    />
  );
};

export default TestSetsTab;
